import { Vector2, Vector3 } from 'three';

import type { ReferencePlane } from '../../reference';

/**
 * 样条偏移几何计算结果。
 */
export interface SplineOffsetGeometryResult {
    /** 偏移后的参考面局部 2D 点列（与输入点列同构，闭合曲线首尾点重合）。 */
    readonly offsetLocalPoints: readonly Vector2[];
    /** 等距样条控制点选取索引（按偏移点列，含首尾；已去重）。 */
    readonly controlPointIndices: readonly number[];
    /** 是否为闭合曲线。 */
    readonly isClosed: boolean;
}

/**
 * 开放曲线端点切向（参考面局部 2D，已归一化）。
 *
 * 说明：miter 偏移对开放曲线端点默认用「首/末采样段差分」近似切向；
 * 当调用方具备解析切向（如抛物线由定义可精确推导端点切向）时传入，
 * 保证端点沿真实法向平移，顶端加盖与相邻实体的连接点不产生差分误差。
 */
export interface SplineOffsetEndpointTangents {
    /** 起点切向（沿采样行进方向，等价于端点差分 neighbor - current 的解析值）。 */
    readonly start: Vector2;
    /** 终点切向（沿采样行进方向，等价于端点差分 current - neighbor 的解析值）。 */
    readonly end: Vector2;
}

/**
 * 样条等距偏移几何服务。
 *
 * 职责（单一）：
 * 1. 把采样曲线点投影到参考面局部 2D，执行 miter 法向偏移；
 * 2. 自相交检测——偏移距离过大导致曲线"打结"时返回 null；
 * 3. 按目标控制点数量均匀采样选取控制点索引；
 * 4. 同时服务创建路径（SketchEntityEquidistantOffset 产新对象）与
 *    派生同步路径（CurveOffsetDerivedSyncService 静默写回既有对象）。
 */
export class SplineOffsetGeometryService {
    /**
     * 计算样条曲线点列的等距偏移。
     *
     * @param curvePoints 源曲线采样点（世界坐标）。
     * @param referencePlane 参考平面。
     * @param distanceMm 偏移距离（毫米，正值）。
     * @param isReverse false=正向（顺时针90°），true=反向（逆时针90°）。
     * @param targetControlPointCount 目标控制点数量（与源样条控制点数量一致）。
     * @param endpointTangents 可选的开放曲线端点解析切向（局部 2D）；
     *  缺省时端点沿用「首/末采样段差分」近似切向（样条等无解析切向的曲线）。
     * @returns 偏移几何结果；点数不足或自相交时返回 null。
     */
    public computeOffset(
        curvePoints: readonly Vector3[],
        referencePlane: ReferencePlane,
        distanceMm: number,
        isReverse: boolean,
        targetControlPointCount: number,
        endpointTangents?: SplineOffsetEndpointTangents,
    ): SplineOffsetGeometryResult | null {
        if (curvePoints.length < 2) {
            return null;
        }

        // 判断闭合：检测首尾点距离平方
        const first = curvePoints[0]!;
        const last = curvePoints[curvePoints.length - 1]!;
        const isClosed = first.distanceToSquared(last) < 1e-14;

        // 单位转换：毫米 → 米
        const offsetDistance = distanceMm * 0.001;

        // 投影到参考面局部 2D 坐标
        const localPoints = curvePoints.map((point) => referencePlane.worldToLocal(point));

        // 执行 miter 逐点偏移
        const offsetLocalPoints = this.computeSplineOffsetPoints2D(
            localPoints,
            isClosed,
            offsetDistance,
            isReverse,
            endpointTangents,
        );

        // 自相交检测：偏移距离过大导致曲线"打结"时返回 null
        if (this.doesOffsetSplineSelfIntersect(offsetLocalPoints, isClosed)) {
            return null;
        }

        const controlPointIndices = this.selectControlPointIndices(
            offsetLocalPoints.length,
            isClosed,
            Math.max(2, targetControlPointCount),
        );

        return {
            offsetLocalPoints,
            controlPointIndices,
            isClosed,
        };
    }

    /**
     * 对局部 2D 样条点列执行 miter 法向偏移。
     *
     * 设计说明：
     * 1. 开放曲线：中间点做 miter 交点偏移，端点做法向平移；
     * 2. 闭合曲线：所有点做带 wrap-around 的 miter 交点偏移，末尾补回偏移后的首点；
     * 3. 偏移方向：正向为切向量顺时针旋转 90° (tangent.y, -tangent.x)，
     *    反向为逆时针旋转 90° (-tangent.y, tangent.x)。
     *
     * @param points 局部 2D 点列。
     * @param isClosed 是否为闭合曲线。
     * @param distance 偏移距离（米）。
     * @param isReverse 是否为反向偏移。
     * @param endpointTangents 可选的开放曲线端点解析切向；缺省用首/末采样段差分。
     * @returns 偏移后的局部 2D 点列，点数与输入一致。
     */
    private computeSplineOffsetPoints2D(
        points: readonly Vector2[],
        isClosed: boolean,
        distance: number,
        isReverse: boolean,
        endpointTangents?: SplineOffsetEndpointTangents,
    ): Vector2[] {
        // 两点退化情况：直接法向平移
        if (points.length === 2) {
            const tangent = new Vector2().subVectors(points[1]!, points[0]!).normalize();
            const normal = this.resolveOffsetNormal2DFromTangent(tangent, isReverse);
            return [
                points[0]!.clone().addScaledVector(normal, distance),
                points[1]!.clone().addScaledVector(normal, distance),
            ];
        }

        // 闭合曲线去掉末尾重复点（后续统一补回）
        const workPoints = isClosed ? points.slice(0, -1) : [...points];
        const m = workPoints.length;
        const result: Vector2[] = [];

        for (let i = 0; i < m; i++) {
            const current = workPoints[i]!;

            if (!isClosed && (i === 0 || i === m - 1)) {
                // 开放曲线端点：法向平移。
                // 切向口径 = 采样行进方向（起点差分 neighbor - current、终点差分 current - neighbor）；
                // 调用方提供解析切向（如抛物线由定义推导）时优先使用，消除差分近似误差，
                // 保证偏移端点与相邻链实体端点严格重合（如抛物线顶点处与线段的光滑衔接）。
                const neighbor = i === 0 ? workPoints[1]! : workPoints[m - 2]!;
                const analyticTangent = i === 0 ? endpointTangents?.start : endpointTangents?.end;
                const tangent = analyticTangent
                    ? analyticTangent.clone().normalize()
                    : (i === 0
                        ? new Vector2().subVectors(neighbor, current).normalize()
                        : new Vector2().subVectors(current, neighbor).normalize());
                const normal = this.resolveOffsetNormal2DFromTangent(tangent, isReverse);
                result.push(current.clone().addScaledVector(normal, distance));
            } else {
                // 中间点（闭合曲线所有点）：miter 交点法
                const prev = workPoints[(i - 1 + m) % m]!;
                const next = workPoints[(i + 1) % m]!;
                const offsetPoint = this.computeMiterOffsetPoint2D(
                    prev, current, next, distance, isReverse,
                );
                result.push(offsetPoint);
            }
        }

        // 闭合曲线：末尾补回偏移后的首点，保持点数与输入一致
        if (isClosed && result.length > 0) {
            result.push(result[0]!.clone());
        }

        return result;
    }

    /**
     * 根据切向量解析偏移法向。
     *
     * 设计说明：
     * 1. 正向（isReverse=false）：切向量顺时针旋转 90°，即 (tangent.y, -tangent.x)；
     * 2. 反向（isReverse=true）：切向量逆时针旋转 90°，即 (-tangent.y, tangent.x)。
     *
     * @param tangent 切向量（已归一化）。
     * @param isReverse 是否为反向。
     * @returns 归一化偏移法向。
     */
    private resolveOffsetNormal2DFromTangent(tangent: Vector2, isReverse: boolean): Vector2 {
        if (isReverse) {
            // 逆时针 90°：(-y, x)
            return new Vector2(-tangent.y, tangent.x);
        }
        // 顺时针 90°：(y, -x)
        return new Vector2(tangent.y, -tangent.x);
    }

    /**
     * 计算单点的 miter 交点偏移。
     *
     * 设计说明：
     * 1. 对前后两条边的偏置线求交，得到偏移后的顶点位置；
     * 2. 两条边平行（或近乎平行）时退化为平均法向偏移；
     * 3. 使用克莱姆法则直接求解交点参数，避免额外矩阵构造。
     *
     * @param prev 前一顶点。
     * @param current 当前顶点。
     * @param next 后一顶点。
     * @param distance 偏移距离（米）。
     * @param isReverse 是否为反向偏移。
     * @returns 偏移后的顶点。
     */
    private computeMiterOffsetPoint2D(
        prev: Vector2,
        current: Vector2,
        next: Vector2,
        distance: number,
        isReverse: boolean,
    ): Vector2 {
        // 计算前后边的方向向量
        const prevDir = new Vector2().subVectors(current, prev).normalize();
        const nextDir = new Vector2().subVectors(next, current).normalize();

        // 计算两条边的偏移法向
        const prevNormal = this.resolveOffsetNormal2DFromTangent(prevDir, isReverse);
        const nextNormal = this.resolveOffsetNormal2DFromTangent(nextDir, isReverse);

        // 两条偏置边的起点：current 沿法向平移 distance
        const prevPointOnLine = current.clone().addScaledVector(prevNormal, distance);
        const nextPointOnLine = current.clone().addScaledVector(nextNormal, distance);

        // 克莱姆法则求 prevPointOnLine + t * prevDir = nextPointOnLine + s * nextDir 的解
        const denom = prevDir.x * nextDir.y - prevDir.y * nextDir.x;

        // 平行或共线：退化为平均法向偏移
        if (Math.abs(denom) < 1e-12) {
            const avgNormal = new Vector2(
                prevNormal.x + nextNormal.x,
                prevNormal.y + nextNormal.y,
            ).normalize();
            // 处理 180° 折返导致的零向量
            if (avgNormal.length() < 0.5) {
                return current.clone().addScaledVector(prevNormal, distance);
            }
            return current.clone().addScaledVector(avgNormal, distance);
        }

        // 求解参数 t
        const delta = new Vector2(
            nextPointOnLine.x - prevPointOnLine.x,
            nextPointOnLine.y - prevPointOnLine.y,
        );
        const t = (delta.x * nextDir.y - delta.y * nextDir.x) / denom;

        return prevPointOnLine.clone().addScaledVector(prevDir, t);
    }

    /**
     * 判断偏移后的样条曲线是否发生自相交。
     *
     * 设计说明：
     * 1. 使用 O(n²) 线段对交叉检测，样条采样点 n 通常 ≤ 200，性能无问题；
     * 2. 相邻段共享端点，跳过 |i - j| ≤ 1 的段对；
     * 3. 闭合曲线额外跳过首尾相邻的段对（i=0 与 j=segmentCount-1）；
     * 4. 自相交意味着偏移距离过大导致曲线"打结"，对应 SolidWorks 中"无法生成等距实体"。
     *
     * @param points 局部 2D 偏移后的点列。
     * @param isClosed 是否为闭合曲线。
     * @returns 自相交返回 true，否则返回 false。
     */
    private doesOffsetSplineSelfIntersect(points: readonly Vector2[], isClosed: boolean): boolean {
        // 少于 4 个点 → 少于 3 段 → 不可能产生非相邻段交叉
        if (points.length < 4) {
            return false;
        }

        // 闭合曲线：首尾采样点精确重合时，点列末尾存在一个与首点坐标相同的重复点。
        // 若不剔除，段 0（p0→p1）与段 n-2（p_{n-2}→p_{n-1}=p0）虽然几何上共享闭合端点 p0
        // （属于相邻段），索引上却相隔 n-2，既不被 |i-j|≤1 跳过，也不被首尾段对跳过，
        // 其共享端点处的叉积精确为 0，会被共线分支误判为"自相交"（详见缺陷分析）。
        // 此处与 computeSplineOffsetPoints2D 中"闭合曲线去掉末尾重复点"的处理保持一致，
        // 去重后段 0 与末段（p_{n-1}→p0）成为真正的首尾相邻段对，由下方跳过逻辑覆盖。
        const workPoints = isClosed
            && points[0]!.distanceToSquared(points[points.length - 1]!) < 1e-14
            ? points.slice(0, -1)
            : [...points];
        const workLength = workPoints.length;

        // 段数：闭合曲线 n 段（首尾相连），开放曲线 n-1 段
        const segmentCount = isClosed ? workLength : workLength - 1;

        for (let i = 0; i < segmentCount; i++) {
            const a1 = workPoints[i]!;
            const a2 = isClosed ? workPoints[(i + 1) % workLength]! : workPoints[i + 1]!;

            // j 从 i+2 开始，确保跳过相邻段（|i - j| ≤ 1）
            for (let j = i + 2; j < segmentCount; j++) {
                // 闭合曲线：跳过首尾相邻的段对
                if (isClosed && i === 0 && j === segmentCount - 1) {
                    continue;
                }

                const b1 = workPoints[j]!;
                const b2 = isClosed ? workPoints[(j + 1) % workLength]! : workPoints[j + 1]!;

                if (this.doLineSegmentsIntersect2D(a1, a2, b1, b2)) {
                    return true;
                }
            }
        }

        return false;
    }

    /**
     * 判断两条二维线段是否相交（含端点共线容差）。
     *
     * 设计说明：
     * 1. 使用跨立实验（cross product 符号判别）判定严格相交；
     * 2. 共线情况检测端点是否落在对方线段上；
     * 3. 容差 1e-8 避免浮点精度导致的误判。
     *
     * @param a1 线段 A 的起点。
     * @param a2 线段 A 的终点。
     * @param b1 线段 B 的起点。
     * @param b2 线段 B 的终点。
     * @returns 相交返回 true。
     */
    private doLineSegmentsIntersect2D(
        a1: Vector2,
        a2: Vector2,
        b1: Vector2,
        b2: Vector2,
    ): boolean {
        const EPS = 1e-8;

        // 计算叉积方向：(a1→a2) × (a1→bi) 和 (b1→b2) × (b1→ai)
        const d1 = this.cross2DSub(a1, a2, b1);
        const d2 = this.cross2DSub(a1, a2, b2);
        const d3 = this.cross2DSub(b1, b2, a1);
        const d4 = this.cross2DSub(b1, b2, a2);

        // 严格跨立：两段各有一端点在对方两侧
        if (d1 * d2 < -EPS && d3 * d4 < -EPS) {
            return true;
        }

        // 共线情况：检测端点是否落在对方线段上
        if (Math.abs(d1) < EPS && this.isPointOnSegment2D(a1, a2, b1)) return true;
        if (Math.abs(d2) < EPS && this.isPointOnSegment2D(a1, a2, b2)) return true;
        if (Math.abs(d3) < EPS && this.isPointOnSegment2D(b1, b2, a1)) return true;
        if (Math.abs(d4) < EPS && this.isPointOnSegment2D(b1, b2, a2)) return true;

        return false;
    }

    /**
     * 计算二维叉积 (p2-p1) × (p3-p1)。
     *
     * @param p1 共享起点。
     * @param p2 第一条边终点。
     * @param p3 第二条边终点。
     * @returns 叉积标量值，正数表示逆时针，负数表示顺时针。
     */
    private cross2DSub(p1: Vector2, p2: Vector2, p3: Vector2): number {
        return (p2.x - p1.x) * (p3.y - p1.y) - (p2.y - p1.y) * (p3.x - p1.x);
    }

    /**
     * 判断点是否在线段的有效范围内（含容差）。
     *
     * @param segStart 线段起点。
     * @param segEnd 线段终点。
     * @param point 待检测点。
     * @returns 点在线段包围盒内返回 true。
     */
    private isPointOnSegment2D(segStart: Vector2, segEnd: Vector2, point: Vector2): boolean {
        const EPS = 1e-8;
        const minX = Math.min(segStart.x, segEnd.x) - EPS;
        const maxX = Math.max(segStart.x, segEnd.x) + EPS;
        const minY = Math.min(segStart.y, segEnd.y) - EPS;
        const maxY = Math.max(segStart.y, segEnd.y) + EPS;
        return point.x >= minX && point.x <= maxX && point.y >= minY && point.y <= maxY;
    }

    /**
     * 按目标控制点数量均匀采样选取控制点索引。
     *
     * 说明：与创建路径同一口径——等距样条控制点数量与源样条保持一致；
     * 闭合样条跳过末尾重复点，相邻重复索引去重。
     *
     * @param offsetPointCount 偏移点列长度。
     * @param isClosed 是否为闭合曲线。
     * @param targetControlPointCount 目标控制点数量。
     * @returns 去重后的控制点索引列表。
     */
    private selectControlPointIndices(
        offsetPointCount: number,
        isClosed: boolean,
        targetControlPointCount: number,
    ): number[] {
        // 闭合样条偏移后首尾重合，采样时跳过末尾重复点以避免重复控制点
        const samplePointCount = isClosed ? offsetPointCount - 1 : offsetPointCount;
        const controlStride = (samplePointCount - 1) / (targetControlPointCount - 1);
        const controlIndices: number[] = [];
        for (let k = 0; k < targetControlPointCount; k++) {
            const idx = Math.round(k * controlStride);
            // 防止浮点舍入越界
            controlIndices.push(Math.min(idx, samplePointCount - 1));
        }
        // 去重：相邻重复索引只保留一个
        return controlIndices.filter(
            (idx, i) => i === 0 || idx !== controlIndices[i - 1],
        );
    }
}
